import { useState, useEffect, useRef, useCallback } from "react";
import { useLazyGetBlotterDropcopyDataQuery } from "../../services/blotterApi";
import type { BlotterFeedType } from "../../services/blotterApi";
import { mapRowData } from "../../components/AlgoTable/utils/mappers";
import { filterBlotterData } from "../../utils/dataFilters";
import { debounce } from "@/utils/helper_functions";
import { useAlerts } from "@/hooks";
import type { Dayjs } from "dayjs";

interface UseBlotterDataProps {
  accountId: string | null | undefined;
  feedType: BlotterFeedType;
  startDate: Dayjs | null;
}

/**
 * Hook to orchestrate data fetching with pagination and performance optimizations.
 *
 * Features:
 * - Lazy query trigger management for manual control
 * - Initial data load
 * - Load by start date (debounced 2500ms to match original performance)
 * - Load more (pagination token-based infinite scroll)
 * - Data filtering (excludes restatements/notifications)
 * - Row data mapping for AG Grid
 *
 * Performance:
 * - Debounces date changes to prevent excessive API calls (2500ms)
 * - Maintains pagination token for efficient infinite scroll
 * - Filters and maps data before setting state
 */
export const useBlotterData = ({ accountId, feedType, startDate }: UseBlotterDataProps) => {
  const [trigger, { error }] = useLazyGetBlotterDropcopyDataQuery();
  const { showError } = useAlerts();

  // State
  const [rowData, setRowData] = useState<any[]>([]);
  const [paginationToken, setPaginationToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [onlyRestatements, setOnlyRestatements] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);

  // Ref for debounced function (persist across renders)
  const debouncedFetchByStartDateRef = useRef<((accountId: string, feedType: BlotterFeedType, startMs: string) => void) | null>(null);

  // Create debounced fetch function (2500ms to match original Blotter performance)
  useEffect(() => {
    debouncedFetchByStartDateRef.current = debounce(
      async (accountId: string, feedType: BlotterFeedType, startMs: string) => {
        setIsInitialLoading(true);
        try {
          const response = await trigger({
            account_id: accountId,
            feed_type: feedType,
            start_ms: startMs,
            direction: "desc",
            limit: 500,
          }).unwrap();

          // Filter data
          const { filtered_data, only_restatements } = filterBlotterData(response.data || []);
          setOnlyRestatements(only_restatements);

          // Map data
          const mapped = mapRowData(filtered_data, feedType);
          setRowData(mapped);

          // Update pagination state
          const token = response.token ? encodeURIComponent(response.token) : null;
          setPaginationToken(token);
          setHasMore(!!response.token);
        } catch (err) {
          showError(`Failed to fetch blotter data: ${err}`);
        } finally {
          setIsInitialLoading(false);
        }
      },
      2500
    );

    // Cleanup: cancel pending debounce on unmount
    return () => {
      debouncedFetchByStartDateRef.current = null;
    };
  }, []); // Empty deps - trigger is stable from RTK Query

  // Fetch initial data when account or feed type changes
  const fetchInitial = useCallback(async () => {
    if (!accountId) {
      setRowData([]);
      setPaginationToken(null);
      setHasMore(false);
      setIsInitialLoading(false);
      return;
    }

    setIsInitialLoading(true);
    try {
      const response = await trigger({
        account_id: accountId,
        feed_type: feedType,
        direction: "desc",
        limit: 500,
      }).unwrap();

      // Filter data
      const { filtered_data, only_restatements } = filterBlotterData(response.data || []);
      setOnlyRestatements(only_restatements);

      // Map data
      const mapped = mapRowData(filtered_data, feedType);
      setRowData(mapped);

      // Update pagination state (don't encode - API will handle encoding)
      setPaginationToken(response.token || null);
      setHasMore(!!response.token);
    } catch (err) {
      showError(`Failed to fetch initial blotter data: ${err}`);
    } finally {
      setIsInitialLoading(false);
    }
  }, [accountId, feedType, trigger, showError]);

  // Fetch more data (pagination)
  const fetchMore = useCallback(async () => {
    if (!accountId || !paginationToken) return;

    try {
      const response = await trigger({
        account_id: accountId,
        feed_type: feedType,
        pagination_token: paginationToken,
      }).unwrap();

      // Filter data
      const { filtered_data, only_restatements } = filterBlotterData(response.data || []);
      setOnlyRestatements(only_restatements);

      // Map data
      const mapped = mapRowData(filtered_data, feedType);

      // Append to existing row data (infinite scroll)
      setRowData((prev) => [...prev, ...mapped]);

      // Update pagination state (don't encode - API will handle encoding)
      setPaginationToken(response.token || null);
      setHasMore(!!response.token);
    } catch (err) {
      showError(`Failed to load more blotter data: ${err}`);
    }
  }, [accountId, feedType, paginationToken, trigger, showError]);

  // Trigger fetch when account, feed type, or start date changes
  useEffect(() => {
    if (!accountId) return;

    if (startDate && debouncedFetchByStartDateRef.current) {
      // Use debounced fetch for date changes
      const startMs = startDate.unix() * 1000;
      debouncedFetchByStartDateRef.current(accountId, feedType, startMs.toString());
    } else {
      // Fetch initial data when no start date (or start date cleared)
      fetchInitial();
    }
  }, [startDate, accountId, feedType, fetchInitial]);

  return {
    rowData,
    isLoading: isInitialLoading,
    error,
    paginationToken,
    hasMore,
    onlyRestatements,
    fetchMore,
    fetchInitial,
  };
};
